import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { portalAuthHeaderValue } from '../utils/tokenUtils';
import { toast, Toaster } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Lock, CheckCircle, XCircle, Loader2, Eye, EyeOff } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Dark theme colors (matching /buchen)
const theme = {
  bg: '#0a0a0a',
  cardBg: '#1a1a1a',
  accent: '#EAB308',
  text: '#ffffff',
  textMuted: '#a0a0a0',
  border: '#2a2a2a'
};

export default function SetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [email, setEmail] = useState('');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [portalUrl, setPortalUrl] = useState('/portal'); // Default to relative path

  useEffect(() => {
    // Load custom portal URL from settings
    const loadPortalSettings = async () => {
      try {
        const response = await axios.get(`${API}/portal/settings/public`, { headers: { Authorization: portalAuthHeaderValue() } });
        // Check if there's a custom URL configured
        if (response.data.custom_portal_url) {
          setPortalUrl(response.data.custom_portal_url);
        }
      } catch (error) {
        console.log('Could not load portal settings, using default URL');
      }
    };
    loadPortalSettings();
  }, []);

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setTokenValid(false);
      setTokenError('Kein Token gefunden');
      return;
    }

    // Verify token
    const verifyToken = async () => {
      try {
        const response = await axios.get(`${API}/public/verify-token/${token}`);
        if (response.data.valid) {
          setTokenValid(true);
          setEmail(response.data.email);
        } else {
          setTokenValid(false);
          setTokenError(response.data.reason === 'expired' 
            ? 'Der Link ist abgelaufen. Bitte kontaktieren Sie das Studio.'
            : 'Der Link ist ungültig oder wurde bereits verwendet.');
        }
      } catch (error) {
        setTokenValid(false);
        setTokenError('Fehler bei der Überprüfung des Links');
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password.length < 6) {
      toast.error('Passwort muss mindestens 6 Zeichen lang sein');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    setSubmitting(true);
    
    try {
      await axios.post(`${API}/public/set-password`, {
        token,
        password
      });
      
      setSuccess(true);
      // Success card is shown - no need for additional toast
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Setzen des Passworts');
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: theme.bg }}>
        <Toaster position="top-center" richColors />
        <Card className="w-full max-w-md border-0" style={{ backgroundColor: theme.cardBg }}>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin" style={{ color: theme.accent }} />
              <p style={{ color: theme.textMuted }}>Link wird überprüft...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid token
  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: theme.bg }}>
        <Toaster position="top-center" richColors />
        <Card className="w-full max-w-md border-0" style={{ backgroundColor: theme.cardBg }}>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-full" style={{ backgroundColor: '#dc262620' }}>
                <XCircle className="h-12 w-12 text-red-500" />
              </div>
            </div>
            <CardTitle className="text-red-500">Link ungültig</CardTitle>
            <CardDescription style={{ color: theme.textMuted }}>{tokenError}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={() => {
                if (portalUrl.startsWith('http')) {
                  window.location.href = portalUrl;
                } else {
                  navigate(portalUrl);
                }
              }} 
              className="w-full"
              style={{ 
                backgroundColor: 'transparent', 
                borderColor: theme.border,
                color: theme.text 
              }}
              variant="outline"
            >
              Zum Kundenportal
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: theme.bg }}>
        <Toaster position="top-center" richColors />
        <Card className="w-full max-w-md border-0" style={{ backgroundColor: theme.cardBg }}>
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 rounded-full" style={{ backgroundColor: `${theme.accent}20` }}>
                <CheckCircle className="h-12 w-12" style={{ color: theme.accent }} />
              </div>
            </div>
            <CardTitle style={{ color: theme.accent }}>Passwort gesetzt!</CardTitle>
            <CardDescription style={{ color: theme.textMuted }}>
              Du kannst dich jetzt im Kundenportal anmelden.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-lg" style={{ backgroundColor: theme.bg }}>
              <p className="text-sm" style={{ color: theme.textMuted }}>
                <strong style={{ color: theme.text }}>E-Mail:</strong> {email}
              </p>
            </div>
            <Button 
              onClick={() => {
                // Use custom portal URL if set, otherwise use relative path
                if (portalUrl.startsWith('http')) {
                  window.location.href = portalUrl;
                } else {
                  navigate(portalUrl);
                }
              }} 
              className="w-full font-semibold"
              style={{ backgroundColor: theme.accent, color: '#000' }}
            >
              Zum Kundenportal →
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Password form
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: theme.bg }}>
      <Toaster position="top-center" richColors />
      <Card className="w-full max-w-md border-0" style={{ backgroundColor: theme.cardBg }}>
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-full" style={{ backgroundColor: `${theme.accent}20` }}>
              <Lock className="h-8 w-8" style={{ color: theme.accent }} />
            </div>
          </div>
          <CardTitle style={{ color: theme.text }}>Passwort setzen</CardTitle>
          <CardDescription style={{ color: theme.textMuted }}>
            Setzen Sie Ihr Passwort für das Kundenportal
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="p-3 rounded-lg mb-4" style={{ backgroundColor: theme.bg }}>
              <p className="text-sm" style={{ color: theme.textMuted }}>
                <strong style={{ color: theme.text }}>E-Mail:</strong> {email}
              </p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password" style={{ color: theme.text }}>Neues Passwort</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mindestens 6 Zeichen"
                  required
                  minLength={6}
                  data-testid="password-input"
                  className="pr-10"
                  style={{ 
                    backgroundColor: theme.bg, 
                    borderColor: theme.border, 
                    color: theme.text 
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: theme.textMuted }}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" style={{ color: theme.text }}>Passwort bestätigen</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Passwort wiederholen"
                  required
                  data-testid="confirm-password-input"
                  className="pr-10"
                  style={{ 
                    backgroundColor: theme.bg, 
                    borderColor: theme.border, 
                    color: theme.text 
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2"
                  style={{ color: theme.textMuted }}
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {password && confirmPassword && password !== confirmPassword && (
              <p className="text-sm text-red-500">Passwörter stimmen nicht überein</p>
            )}

            {password && password.length < 6 && (
              <p className="text-sm" style={{ color: theme.accent }}>Mindestens 6 Zeichen erforderlich</p>
            )}
            
            <Button 
              type="submit" 
              className="w-full font-semibold"
              disabled={submitting || password.length < 6 || password !== confirmPassword}
              data-testid="set-password-btn"
              style={{ backgroundColor: theme.accent, color: '#000' }}
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Wird gespeichert...
                </>
              ) : (
                'Passwort setzen'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
